// WIB timezone for all date/time display
const TZ = 'Asia/Jakarta';

export const formatRupiah = (amount) => {
  if (!amount && amount !== 0) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
};

// Split nominal jadi prefix (Rp / -Rp) + angka — dipakai komponen <Rupiah />
// untuk tampilan rata kiri (Rp) + rata kanan (angka).
export const splitRupiah = (amount) => {
  const n = Number(amount) || 0;
  const isNeg = n < 0;
  const num = new Intl.NumberFormat('id-ID').format(Math.abs(n));
  return { prefix: isNeg ? '-Rp' : 'Rp', number: num, isNeg };
};

export const formatDate = (date, format = 'short') => {
  if (!date) return '-';
  const d = new Date(date);
  if (format === 'short') return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ });
  // 'medium' → "8 Mei 2026" (tanggal tanpa angka nol di depan)
  if (format === 'medium') return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ });
  if (format === 'long') return d.toLocaleDateString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: TZ });
  if (format === 'time') return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: TZ }) + ' WIB';
  if (format === 'datetime') return `${formatDate(date, 'short')} ${formatDate(date, 'time')}`;
  // Ringkas untuk tabel: 'numeric' → "01/07/26", 'numdatetime' → "01/07/26 21:00"
  if (format === 'numeric') return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: TZ });
  if (format === 'numdatetime') return `${formatDate(date, 'numeric')} ${d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', timeZone: TZ })}`;
  return d.toLocaleDateString('id-ID', { timeZone: TZ });
};

export const suggestCash = (total) => {
  const suggestions = [];
  const roundings = [50000, 100000, 150000, 200000, 300000, 500000, 1000000];
  for (const r of roundings) {
    if (r >= total) { suggestions.push(r); if (suggestions.length === 4) break; }
  }
  if (suggestions.length < 4) {
    const next = Math.ceil(total / 100000) * 100000;
    if (!suggestions.includes(next)) suggestions.unshift(next);
  }
  return [...new Set(suggestions)].slice(0, 4);
};

export const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export const expenseCategoryLabel = (cat) => ({
  rent: 'Sewa', electricity: 'Listrik', water: 'Air', supplies: 'Perlengkapan',
  salary: 'Gaji', maintenance: 'Perawatan', marketing: 'Marketing', other: 'Lainnya',
}[cat] || cat);

/**
 * Jarak hari dalam bahasa manusia: "Hari ini", "Kemarin", "3 hari lalu".
 *
 * Dihitung dari selisih TANGGAL KALENDER, bukan selisih milidetik. Setoran
 * pukul 23.00 kemarin dan pukul 01.00 hari ini cuma berjarak 2 jam, tapi yang
 * benar dibaca orang tetap "Kemarin" — bukan "0 hari lalu".
 *
 * Lewat 3 bulan diringkas jadi bulan supaya "412 hari lalu" tidak perlu dibaca
 * angkanya satu per satu untuk tahu bahwa itu sudah lama sekali.
 *
 * @returns {{ teks: string, hari: number } | null} null kalau tanggalnya kosong
 */
export const hariLalu = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  // Tanggal kalender WIB di dua sisi — dengan jam perangkat, HP yang zonanya
  // bukan WIB menyebut "Kemarin" untuk setoran yang di cabang terjadi hari ini.
  const hari = Math.round((Date.parse(tanggalLokal()) - Date.parse(tanggalLokal(d))) / 86400000);

  if (hari < 0) return { teks: 'Terjadwal', hari };
  if (hari === 0) return { teks: 'Hari ini', hari };
  if (hari === 1) return { teks: 'Kemarin', hari };
  if (hari < 90) return { teks: `${hari} hari lalu`, hari };
  const bulan = Math.round(hari / 30);
  return { teks: `${bulan} bulan lalu`, hari };
};

// ════════════════════════════════════════════════════════════════════
// TANGGAL & JAM WIB — satu-satunya pintu untuk "hari ini", awal bulan,
// tahun berjalan, dan jam yang ditampilkan.
//
// Semua cabang & jam bisnis Fast Track memakai WIB, jadi hasilnya WAJIB sama di
// laptop HO, tablet kasir, dan HP yang zona waktunya kebetulan WITA/WIT/UTC.
// Dua jebakan yang dulu tersebar di puluhan halaman:
//
//   • `new Date().toISOString().slice(0, 10)` → tanggal UTC. WIB lebih cepat
//     7 jam, jadi pukul 00:00–07:00 WIB nilainya masih KEMARIN: filter laporan
//     mundur sehari, isian hari ini ditolak "melebihi hari ini".
//   • `getFullYear/getMonth/getDate/getHours` dan `new Date(y, m, 1)` → jam
//     PERANGKAT. Di HP ber-zona WITA, "awal bulan" yang dirakit dari jam
//     perangkat lalu diubah ke WIB jatuh ke tanggal terakhir bulan LALU.
//
// Jangan menghitung "hari ini" di level modul (const di luar komponen): tab
// admin keep-alive bisa terbuka melewati tengah malam dan terus memakai
// tanggal kemarin sampai halamannya dimuat ulang.
// ════════════════════════════════════════════════════════════════════

const HARI_KE_ANGKA = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const PEMECAH_WIB = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'short' });

/**
 * Bagian tanggal & jam menurut WIB.
 * @returns {{ tahun:number, bulan:number, tanggal:number, jam:number, menit:number, detik:number, hari:number } | null}
 *   bulan 1–12, hari 0 = Minggu. null kalau tanggalnya tidak valid.
 */
export const bagianWIB = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  if (!x || Number.isNaN(x.getTime())) return null;
  const p = Object.fromEntries(PEMECAH_WIB.formatToParts(x).map((q) => [q.type, q.value]));
  return { tahun: +p.year, bulan: +p.month, tanggal: +p.day, jam: +p.hour % 24, menit: +p.minute, detik: +p.second, hari: HARI_KE_ANGKA[p.weekday] };
};

/**
 * Tanggal 'YYYY-MM-DD' menurut WIB. Juga untuk mengisi <input type="date"> dari
 * timestamp tersimpan: tanggal yang disimpan sebagai 00:00 WIB (= 17:00Z sehari
 * sebelumnya) terbaca mundur sehari kalau lewat toISOString().
 * @returns {string} '' kalau tanggalnya tidak valid
 */
export const tanggalWIB = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  if (!x || Number.isNaN(x.getTime())) return '';
  return x.toLocaleDateString('en-CA', { timeZone: TZ });
};

/** Bulan 'YYYY-MM' menurut WIB. */
export const bulanWIB = (d = new Date()) => tanggalWIB(d).slice(0, 7);

/** Tahun (angka) menurut WIB. */
export const tahunWIB = (d = new Date()) => bagianWIB(d)?.tahun;

/** Bulan (angka 1–12) menurut WIB. */
export const bulanAngkaWIB = (d = new Date()) => bagianWIB(d)?.bulan;

/** 'YYYY-MM-01' bulan berjalan (WIB). */
export const awalBulanWIB = (d = new Date()) => `${bulanWIB(d)}-01`;

/** 'YYYY-01-01' tahun berjalan (WIB). */
export const awalTahunWIB = (d = new Date()) => `${tahunWIB(d)}-01-01`;

/** Tanggal terakhir sebuah bulan 'YYYY-MM' → 'YYYY-MM-DD'. Aritmetika kalender murni. */
export const akhirBulan = (ym) => {
  const [y, m] = String(ym).split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

/**
 * Geser tanggal 'YYYY-MM-DD' sebanyak `n` hari (boleh negatif). Aritmetika
 * kalender murni lewat UTC — tidak dipengaruhi zona perangkat maupun DST.
 * Contoh kemarin: geserTanggal(tanggalWIB(), -1).
 */
export const geserTanggal = (ymd, n) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

/** Hari dalam minggu (0 = Minggu) dari 'YYYY-MM-DD'. */
export const hariDariTanggal = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

/**
 * Tanggal 'YYYY-MM-DD' + jam 'HH:MM' yang DIMAKSUD WIB → Date. Untuk isian
 * tanggal/jam yang dikirim ke server (mis. jadwal kirim notifikasi): tanpa
 * +07:00, `new Date('2026-09-14T10:00')` dibaca jam perangkat.
 */
export const dariWIB = (ymd, hhmm = '00:00') => new Date(`${ymd}T${String(hhmm).slice(0, 5)}:00+07:00`);

/** Jam 'HH:MM' (24 jam) menurut WIB, tanpa akhiran. '' kalau kosong/tidak valid. */
export const jamWIB = (d) => {
  const p = d ? bagianWIB(d) : null;
  return p ? `${String(p.jam).padStart(2, '0')}:${String(p.menit).padStart(2, '0')}` : '';
};

// Nama lama — sudah dipakai puluhan halaman. Dulu mengikuti jam PERANGKAT;
// sekarang sama persis dengan versi WIB di atas.
export const tanggalLokal = tanggalWIB;
export const bulanLokal = bulanWIB;
